import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Trophy, CarSimple } from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * GlobalPopups — mounted at the App root.
 * Currently handles two server-driven popups:
 *  1. VIP reward earned (Task 8) — fires whenever the user crosses the 5,000 Lek threshold.
 *  2. Cash subscription renewal alerts (Task 19) — fires at login for 5d/1d/expired.
 * Both popups are driven by lightweight GETs and dismissed via POST endpoints so they
 * don't re-fire on every navigation.
 */
export default function GlobalPopups() {
  const { user } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [vipPopup, setVipPopup] = useState(null);
  const [renewalAlert, setRenewalAlert] = useState(null);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    Promise.all([
      axios.get(`${API}/vip/pending-reward`, { withCredentials: true }).catch(() => ({ data: { popup: null } })),
      axios.get(`${API}/me/renewal-alert`, { withCredentials: true }).catch(() => ({ data: { alert: null } })),
    ]).then(([vipRes, renewRes]) => {
      if (cancelled) return;
      if (vipRes.data?.popup) setVipPopup(vipRes.data.popup);
      if (renewRes.data?.alert) setRenewalAlert(renewRes.data.alert);
    });
    return () => { cancelled = true; };
  }, [user]);

  const dismissVip = async () => {
    setVipPopup(null);
    try { await axios.post(`${API}/vip/dismiss-reward`, {}, { withCredentials: true }); } catch (err) { console.warn('[VIP] dismiss-reward failed', err?.response?.status); }
  };

  const closeRenewal = () => setRenewalAlert(null);

  const renewalCopy = () => {
    if (!renewalAlert) return { title: '', body: '' };
    if (renewalAlert.kind === 'expired') {
      return { title: t('renewalExpiredTitle'), body: t('renewalExpiredBody'), kind: 'expired' };
    }
    if (renewalAlert.kind === 'expiring_1d') {
      return { title: t('renewalExpiringTitle'), body: t('renewalExpiring1dBody').replace('{n}', renewalAlert.days_left ?? 1), kind: '1d' };
    }
    return { title: t('renewalExpiringTitle'), body: t('renewalExpiring5dBody').replace('{n}', renewalAlert.days_left ?? 5), kind: '5d' };
  };
  const renewal = renewalCopy();

  return (
    <>
      {/* VIP reward popup */}
      <Dialog open={!!vipPopup} onOpenChange={(o) => !o && dismissVip()}>
        <DialogContent
          className="bg-[#121212] max-w-sm mx-auto border-[#FFD700]/40"
          data-testid="vip-reward-popup"
        >
          <DialogHeader>
            <div className="w-20 h-20 mx-auto rounded-3xl bg-gradient-to-br from-[#FFD700]/30 to-[#FF5A00]/30 flex items-center justify-center mb-2"
                 style={{ boxShadow: '0 0 36px rgba(255, 215, 0, 0.45)' }}>
              <Trophy size={42} weight="fill" className="text-[#FFD700]" />
            </div>
            <DialogTitle className="text-center text-xl font-['Outfit'] text-[#FFD700]">
              {t('vipRewardPopupTitle')}
            </DialogTitle>
          </DialogHeader>
          <p className="text-center text-white/85 text-sm leading-relaxed px-2 mt-1" data-testid="vip-reward-body">
            {t('vipRewardPopupBody')}
          </p>
          {vipPopup?.total_free > 0 && (
            <p className="text-center text-[#FFD700] text-xs mt-2 font-semibold">
              {t('vipRewardCredit').replace('{n}', vipPopup.total_free)}
            </p>
          )}
          <p className="text-center text-white/45 text-[10px] mt-3 flex items-center justify-center gap-1">
            <CarSimple size={11} /> {t('portalScaloOrder')}
          </p>
          <Button
            onClick={dismissVip}
            className="w-full h-12 mt-3 rounded-2xl bg-[#FFD700] hover:bg-[#FFE552] text-black font-semibold"
            data-testid="vip-reward-close"
          >
            OK
          </Button>
        </DialogContent>
      </Dialog>

      {/* Renewal alert popup */}
      <Dialog open={!!renewalAlert} onOpenChange={(o) => !o && closeRenewal()}>
        <DialogContent
          className={`bg-[#121212] max-w-sm mx-auto ${renewal.kind === 'expired' ? 'border-red-500/40' : 'border-[#FF5A00]/40'}`}
          data-testid="renewal-alert-popup"
        >
          <DialogHeader>
            <DialogTitle className="text-center text-lg font-['Outfit']">
              {renewal.title}
            </DialogTitle>
          </DialogHeader>
          <p className="text-center text-white/80 text-sm leading-relaxed px-2 mt-1" data-testid="renewal-alert-body">
            {renewal.body}
          </p>
          <div className="grid grid-cols-2 gap-2 mt-3">
            <Button
              onClick={closeRenewal}
              variant="outline"
              className="h-11 border-white/10 text-white/80 rounded-xl"
              data-testid="renewal-alert-later"
            >
              {t('renewLater')}
            </Button>
            <Button
              onClick={() => { closeRenewal(); navigate('/subscriptions'); }}
              className="h-11 rounded-xl bg-[#0ABAB5] text-black font-semibold"
              data-testid="renewal-alert-renew"
            >
              {t('renewWithCard')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
